import {
  collection,
  doc,
  writeBatch,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { CampaignLinkRecord } from '../types/emailLinkAnalytics';

const COLLECTION = 'campaign_links';

export class CampaignLinkService {
  /** Replace link registry for a campaign (called once per send). */
  static async saveCampaignLinks(links: CampaignLinkRecord[]): Promise<void> {
    if (links.length === 0) return;

    const campaignId = links[0].campaignId;
    await this.deleteCampaignLinks(campaignId);

    const batch = writeBatch(db);
    const now = serverTimestamp();

    for (const link of links) {
      const ref = doc(db, COLLECTION, link.linkId);
      batch.set(ref, {
        ...link,
        createdAt: now,
        updatedAt: now,
      });
    }

    await batch.commit();
    console.log(`📎 Saved ${links.length} campaign links for campaign ${campaignId}`);
  }

  static async deleteCampaignLinks(campaignId: string): Promise<void> {
    const existing = await getDocs(
      query(collection(db, COLLECTION), where('campaignId', '==', campaignId))
    );
    if (existing.empty) return;

    const batch = writeBatch(db);
    existing.docs.forEach((snap) => batch.delete(snap.ref));
    await batch.commit();
  }

  static async getCampaignLinks(campaignId: string): Promise<CampaignLinkRecord[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTION), where('campaignId', '==', campaignId))
    );
    return snapshot.docs
      .map((d) => d.data() as CampaignLinkRecord)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  static async getWorkspaceCampaignLinks(
    workspaceId: string,
    limitCount = 500
  ): Promise<CampaignLinkRecord[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTION), where('workspaceId', '==', workspaceId))
    );
    return snapshot.docs
      .map((d) => d.data() as CampaignLinkRecord)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .slice(0, limitCount);
  }
}

export const campaignLinkService = CampaignLinkService;
